import {
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useAccount,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { useEffect, useState } from "react";

import axios from "axios";
import { PropTypes } from "prop-types";
import { dispatchAuthStatusEvent } from "src/config/axios";
import { useQuery } from "react-query";
import { nuclearn_user_token } from "src/App";
import { getAuthConfig } from "../AuthConfig";

var nuclearn_api_url = window.ENV.NUCLEARN_API_URL;

const login_url = nuclearn_api_url + "/auth/login-azure-ad";

// Debug flag to simulate token expiration - set to true to test token refresh logic
const SIMULATE_TOKEN_EXPIRATION = false;

// Counter to track how many times we've attempted to get a token (for simulation)
let tokenAttemptCounter = 0;

// Utility function to enable token expiration simulation for testing
const enableTokenExpirationSimulation = () => {
  console.log("SIMULATION: Token expiration simulation ENABLED");
  console.log("SIMULATION: Next 2 token requests will fail with 401 errors");
  // Reset counter when enabling simulation
  tokenAttemptCounter = 0;
  // We can't modify const, so we'll use a different approach
  window.NUCLEARN_SIMULATE_TOKEN_EXPIRATION = true;
};

// Utility function to disable token expiration simulation
const disableTokenExpirationSimulation = () => {
  console.log("SIMULATION: Token expiration simulation DISABLED");
  window.NUCLEARN_SIMULATE_TOKEN_EXPIRATION = false;
  tokenAttemptCounter = 0;
};

// Check if simulation is enabled (either via const or window variable)
const isSimulationEnabled = () => {
  return SIMULATE_TOKEN_EXPIRATION || window.NUCLEARN_SIMULATE_TOKEN_EXPIRATION;
};

export const useMSALInstance = () => {
  return getAuthConfig(nuclearn_api_url).then((ac) => {
    if (!ac) {
      console.error("MSAL Config is missing required parameters.");
      return null;
    }
    let msalConfig = {
      auth: {
        clientId: ac.params.azure_client_id,
        authority: ac.params.token_authority_template?.replace(
          "{azure_tenant_id}",
          ac.params.azure_tenant_id,
        ),
      },
      cache: { cacheLocation: ac.cache?.location ?? "localStorage" },
    };

    if (
      msalConfig.auth.authority === undefined ||
      msalConfig.auth.clientId === undefined
    ) {
      console.error(
        "MSAL Config is missing required parameters.  Please check your configuration.",
      );
      return null;
    }
    const scopes = ac.params.token_scopes ?? [
      `${ac.params.azure_api_uri_id}/API.READ`,
    ];
    loginRequest = { scopes };

    return new PublicClientApplication(msalConfig);
  });
};

export let loginRequest = {
  scopes: ["api://nuclearnapi/API.READ"],
};

export const getNuclearnAuthZToken = (azureAuthNToken) => {
  // Increment counter for simulation purposes
  tokenAttemptCounter++;

  // Simulate token expiration for testing purposes
  if (isSimulationEnabled()) {
    console.log(`SIMULATION: Token attempt #${tokenAttemptCounter}`);

    // Simulate expired token on first few attempts to test refresh logic
    if (tokenAttemptCounter <= 2) {
      console.log("SIMULATION: Simulating expired token (401 error)");
      const simulatedError = new Error(
        "Simulated token expiration for testing",
      );
      simulatedError.response = {
        status: 401,
        data: { message: "Simulated token expiration for testing" },
      };
      return Promise.reject(simulatedError);
    } else {
      console.log("SIMULATION: Allowing token request to succeed");
    }
  }

  // now that we have the azure authentication token, we need to request the authorization token from Nuclearn Backend.
  // post a request to the backend with the authn token to get this
  // backend will authenticate the authn token
  //
  // send post
  return axios
    .post(login_url, { access_token: azureAuthNToken, token_type: "bearer" })
    .then((response) => {
      // don't rerender state here without user details being set first in browser storage
      localStorage.setItem(
        nuclearn_user_token,
        response.data.token_type + " " + response.data.access_token,
      );

      return response.data.token_type + " " + response.data.access_token;
    })
    .catch((error) => {
      dispatchAuthStatusEvent("", error.response.data.detail);
      return Promise.reject(error);
    });
};

const useGetNuclearnAuthZToken = (authNToken) => {
  return useQuery(
    "authZToken",
    () => {
      return getNuclearnAuthZToken(authNToken);
    },
    { refetchOnWindowFocus: false, enabled: !!authNToken },
  );
};

const WrappedAzureADLogin = (props) => {
  // component needed to wrap our children in MsalProvider, MsalProvider is suggested by MS docs to wrap <App> but we want to isolate it to this file
  const [msalInstance, setMSALInstance] = useState(null);

  useEffect(() => {
    const getMSAL = async () => {
      const _msalInstance = await useMSALInstance();
      setMSALInstance(_msalInstance);
    };
    getMSAL();
  }, []);

  return (
    msalInstance && (
      <MsalProvider instance={msalInstance}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            flexDirection: "column",
            alignContent: "center",
          }}
        >
          <AzureADLogin {...props}></AzureADLogin>
        </div>
      </MsalProvider>
    )
  );
};

const AzureADLogin = ({ setError, onAuthorized }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [azureAuthNToken, setAuthNToken] = useState(null);
  const azureADAccount = useAccount(accounts[0] || {});
  const isAuthenticatedToAzureAD = useIsAuthenticated(azureADAccount);
  const [loginStatus, setLoginStatus] = useState(null);
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);

  // Listen for auth status events from axios interceptor
  useEffect(() => {
    const handleAuthStatusUpdate = (event) => {
      const { status, error } = event.detail;
      setLoginStatus(status);

      // If there's an error, also call setError if provided
      if (error && setError && error.message) {
        setError({
          show: true,
          message: error.message || status,
          error: error,
        });
      } else if (error && setError) {
        setError({
          show: true,
          message: error,
          error: error,
        });
      }
    };

    window.addEventListener("authStatusUpdate", handleAuthStatusUpdate);

    return () => {
      window.removeEventListener("authStatusUpdate", handleAuthStatusUpdate);
    };
  }, [setError]);

  // retrieve a new NL AuthZ token from the backend each time...
  const {
    data: nuclearnAuthZToken,
    isLoading: nuclearnAuthZTokenIsLoading,
    isError: nuclearnAuthZTokenIsError,
    isSuccess: nuclearnAuthZTokenSuccess,
  } = useGetNuclearnAuthZToken(azureAuthNToken);

  // console.log("inProgress: ", inProgress);
  // console.log("isAuthenticatedToAzureAD: ", isAuthenticatedToAzureAD);
  // console.log("MSAL instance", instance);
  // console.log("Account", azureADAccount);
  // console.log("azureAuthNToken Token", azureAuthNToken);
  // console.log("nuclearnAuthZToken Token", nuclearnAuthZToken);

  useEffect(() => {
    if (nuclearnAuthZToken) {
      setLoginStatus("Nuclearn AuthZ Token Received");
    }
    if (nuclearnAuthZTokenSuccess) {
      setLoginStatus("Nuclearn AuthZ Token Received");
    }
    if (nuclearnAuthZTokenIsError) {
      setLoginStatus("Error getting Nuclearn AuthZ Token");
    }
    if (nuclearnAuthZTokenIsLoading) {
      setLoginStatus("Getting Nuclearn AuthZ Token");
    }
  }, [
    nuclearnAuthZTokenSuccess,
    nuclearnAuthZTokenIsError,
    nuclearnAuthZTokenIsLoading,
    nuclearnAuthZToken,
  ]);

  function inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
  const isInIframe = inIframe();

  // In the Azure AD flow the platform token slot (nuclearn_user_token) is
  // written only by getNuclearnAuthZToken and renewed only by the axios 401
  // path (other providers, e.g. helpers/OAuth, write it themselves). Do not
  // add a timer here: a proactive Azure refresh once overwrote the slot with
  // the Azure token and kicked users to login mid-session.
  // This hook call will prompt for login and login the user and get a token if we aren't already logged in
  useEffect(() => {
    if (!isInIframe) {
      // Don't run this is an iframe!  Sometimes this page can be rendered in an iFrame when running the silent acquire token flow
      // based on the redirect_uri, so detect and don't try anything if it's in an iFrame
      if (inProgress === InteractionStatus.HandleRedirect) {
        // We need to save when MSAL handles a redirect
        // Unfortunately, when MSAL handles a redirect with authentication, it's inProgress
        // state updates look like this:
        //  startup
        //  handleRedirect
        //  none    isAuthenticatedToAzureAD=False
        //      * some period of time *
        //  none    isAuthenticatedToAzureAD=True
        //
        // We can't differentiate this from the original state updates when the redirect has not been called, which looks like:
        //   startup
        //   none   isAuthenticatedToAzureAD=False
        //      * can wait forever but we won't get an authenticated user
        // So basically we need to track if we've handled a redirect.  If we have, we can wait for MSAL to report that the user is
        // authenticated.  But if we haven't seen a redirect and the user isn't authenticated, then we need to make a redirect happen.
        setHasHandledRedirect(true);
      } else if (inProgress === InteractionStatus.None) {
        // Need to wait until InteractionStatus.None.  MSAL renders this component multiple times while its starting up,
        // and we want to wait until it's done before testing whether the current user is authenticated
        if (!isAuthenticatedToAzureAD) {
          if (hasHandledRedirect) {
            // We went through the redirect flow and the user isn't authenticated.  Something is wrong.
            // We were setting an error here but this caused the error to show up in successful login cases so we defaulted to a log for when we need it
            // since the error did not directly affect the flow.
            console.error(
              "Error getting Azure Authentication Token from Redirect",
            );
          } else {
            // We haven't been through the redirect flow and the user isn't authenticated, redirect login
            setLoginStatus("Redirect logging into Azure AD");
            instance
              .acquireTokenRedirect(loginRequest)
              .catch((token_request_error) => {
                // Without this the rejection is silent and the user is stuck
                // on the "Redirect logging into Azure AD" status forever
                // (e.g. pkce_not_created on insecure contexts, FRONTEND-C6)
                console.error(
                  "Azure ID Token Request Error: ",
                  token_request_error,
                );
                setError({
                  show: true,
                  message: token_request_error.errorMessage,
                  error: token_request_error,
                });
              });
          }
        } else {
          // We have a valid user, we should be able to get a token silently
          setLoginStatus("Silently Fetching Azure Authentication Token");
          instance
            .acquireTokenSilent({ ...loginRequest, account: azureADAccount })
            .then((response) => {
              // Success!
              setLoginStatus("Azure authentication token received");
              setAuthNToken(response.accessToken);
            })
            .catch((token_request_error) => {
              // When the silent token request fails, we should try to do the acquireTokenRedirect flow again
              // In theory this is a infinite loop if the redirect login works, but the silent token request fails
              // But this is really all we can do!
              console.error(
                "Azure ID Token Request Error: ",
                token_request_error,
              );
              setError({
                show: true,
                message: token_request_error.errorMessage,
                error: token_request_error,
              });
              instance
                .acquireTokenPopup({
                  ...loginRequest,
                  account: azureADAccount,
                })
                .then((response) => {
                  // Success!
                  setLoginStatus("Azure authentication token received");
                  setAuthNToken(response.accessToken);
                })
                .catch((_token_request_error) => {
                  console.error(
                    "Azure ID Token Request Error: ",
                    _token_request_error,
                  );
                  setError({
                    show: true,
                    message: _token_request_error.errorMessage,
                    error: _token_request_error,
                  });
                });
            });
        }
      }
    }
  }, [inProgress, isAuthenticatedToAzureAD, azureADAccount, isInIframe]);

  useEffect(() => {
    if (azureAuthNToken && nuclearnAuthZToken) {
      // Login and token acquisition is complete.  Call the onAuthorized callback.
      setLoginStatus("Login flow complete.");
      onAuthorized();
    }
  }, [azureAuthNToken, nuclearnAuthZToken]);

  switch (loginStatus) {
    default:
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div>{loginStatus}</div>
        </div>
      );
  }
};

AzureADLogin.propTypes = {
  setError: PropTypes.func,
  onAuthorized: PropTypes.func,
};

export default WrappedAzureADLogin;
